import { Fragment, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import type { CommutePlan, CommuteSnapshot, PickupState } from "../../shared/types";
import { cardStatus, onwardStatus, planTag, previewTag, signedMinutes, stationLabel, type CardMode, type CardStatus, type OnwardLeg } from "../../core/format";
import { type NavTarget } from "../navigation";
import { Clock } from "./Clock";
import { NavigateLinks } from "./NavigateLinks";
import { PickupBlock } from "./Pickup";

interface PlanDeckProps {
	snapshot: CommuteSnapshot;
	/** The group being browsed: the live options, or the target-day preview. */
	plans: CommutePlan[];
	/** True when `plans` describes another day, so countdowns and urgency do not apply. */
	preview: boolean;
	/** Which question the card should answer, from where the phone is. */
	mode: CardMode;
	nowMs: number;
	selectedId: string | null;
	onSelect: (id: string) => void;
	/** Which stop he is getting off at. One answer for the whole deck, and for the schedule below it. */
	destinationIndex: number;
	onChooseDestination: (index: number) => void;
	/**
	 * Where the car is being sent, or null when there is no drive left to make.
	 *
	 * Passed in rather than taken from the profile, which only ever knows the boarding station. The
	 * card kept offering Civic Center to a driver who had already been there and dropped somebody
	 * off; where he is going next is home, and only the page above knows whose phone this is.
	 */
	driveTarget?: NavTarget | null;
	/** The train he says he is on. */
	pinnedId?: string | null;
	/** The train the phone's own record points at, which is not always the same one. */
	guessedId?: string | null;
	/** Today's meeting, shown inside the card because it belongs to this moment. */
	pickup?: PickupState | null;
	onRequestPickup?: () => void;
	onStandDownPickup?: () => void;
	/** Factual GPS-backed copy for the pinned onboard train after its ETA expires. */
	cardOverride?: CardStatus | null;
	/** Whether one more "show more trains" tap could still turn up something new today. */
	moreAvailable?: boolean;
	onLoadMore?: () => void;
	/** True while a tap's wider request is still in flight, so a second tap cannot double up. */
	loadingMore?: boolean;
}

/**
 * Horizontal train picker.
 *
 * This is a native scroll-snap container rather than a custom touch handler on purpose. iOS
 * Safari already arbitrates between a horizontal drag inside a scroller and a vertical drag on
 * the page, and it does it better than any `touchmove` listener that has to guess. Nothing here
 * calls `preventDefault`, so the two axes never fight.
 */
export function PlanDeck({ snapshot, plans, preview, mode, nowMs, selectedId, onSelect, destinationIndex, onChooseDestination, driveTarget = null, pinnedId = null, guessedId = null, pickup = null, onRequestPickup, onStandDownPickup, cardOverride = null, moreAvailable = false, onLoadMore, loadingMore = false }: PlanDeckProps) {
	const scrollerRef = useRef<HTMLDivElement>(null);
	const settleRef = useRef<number | undefined>(undefined);
	const programmaticRef = useRef(false);
	/*
	 * Only a swipe may change the train.
	 *
	 * The deck also moves because its contents changed — reaching the station adds two departed
	 * trains, and a train leaving the deck shifts every card behind it — and the scroller keeps its
	 * offset through both, so the card at the centre becomes a different train with no finger
	 * involved. On 2026-09-01 that rewrote his pinned train three times, once onto a train he was
	 * not on for the rest of the ride.
	 */
	const touchingRef = useRef(false);
	const gestureRef = useRef(false);
	const beginGesture = useCallback(() => {
		touchingRef.current = true;
		gestureRef.current = true;
	}, []);

	// On the window, because a finger that leaves the deck still ends the gesture.
	useEffect(() => {
		const end = () => {
			touchingRef.current = false;
		};
		for (const name of ["pointerup", "pointercancel", "touchend", "touchcancel"]) {
			window.addEventListener(name, end);
		}
		return () => {
			for (const name of ["pointerup", "pointercancel", "touchend", "touchcancel"]) {
				window.removeEventListener(name, end);
			}
		};
	}, []);

	// Membership, not length: the position the scroller is holding means nothing once the cards
	// under it have changed identity.
	const plansKey = plans.map((plan) => plan.id).join("|");
	const selectedIndex = Math.max(
		0,
		plans.findIndex((plan) => plan.id === selectedId),
	);

	useLayoutEffect(() => {
		const scroller = scrollerRef.current;
		const card = scroller?.children[selectedIndex] as HTMLElement | undefined;
		if (!scroller || !card) {
			return;
		}
		if (Math.abs(scroller.scrollLeft - card.offsetLeft) < 4) {
			return;
		}
		if (!touchingRef.current) {
			gestureRef.current = false;
		}
		programmaticRef.current = true;
		scroller.scrollTo({ left: card.offsetLeft, behavior: "auto" });
		window.setTimeout(() => {
			programmaticRef.current = false;
		}, 120);
	}, [selectedIndex, plansKey, snapshot.profile.id]);

	const handleScroll = useCallback(() => {
		if (programmaticRef.current) {
			return;
		}
		window.clearTimeout(settleRef.current);
		settleRef.current = window.setTimeout(() => {
			const scroller = scrollerRef.current;
			if (!scroller || !gestureRef.current) {
				return;
			}
			const cards = [...scroller.children] as HTMLElement[];
			const centre = scroller.scrollLeft + scroller.clientWidth / 2;
			const nearest = cards.reduce((best, card, index) => {
				const distance = Math.abs(card.offsetLeft + card.clientWidth / 2 - centre);
				return distance < best.distance ? { index, distance } : best;
			}, { index: 0, distance: Number.POSITIVE_INFINITY });
			const plan = plans[nearest.index];
			if (plan && plan.id !== selectedId) {
				onSelect(plan.id);
			}
			// A slow drag settles more than once, so the gesture only ends when the finger has.
			if (!touchingRef.current) {
				gestureRef.current = false;
			}
		}, 90);
	}, [onSelect, selectedId, plans]);

	useEffect(() => () => window.clearTimeout(settleRef.current), []);

	return (
		<div className="deck">
			<div className="deck__scroller" ref={scrollerRef} onScroll={handleScroll} onPointerDown={beginGesture}>
				{plans.map((plan) => (
					<PlanCard
						key={plan.id}
						plan={plan}
						snapshot={snapshot}
						nowMs={nowMs}
						preview={preview}
						mode={mode}
						destinationIndex={destinationIndex}
						onChooseDestination={onChooseDestination}
						driveTarget={driveTarget}
						pinnedId={pinnedId}
						guessedId={guessedId}
						pickup={pickup}
						onRequestPickup={onRequestPickup}
						onStandDownPickup={onStandDownPickup}
						cardOverride={plan.id === pinnedId ? cardOverride : null}
					/>
				))}
				{moreAvailable ? (
					<article className="card card--more">
						<button type="button" className="card__more" onClick={onLoadMore} disabled={loadingMore} aria-busy={loadingMore}>
							<span className="card__moreLabel">{loadingMore ? "Looking further out…" : "Show more trains"}</span>
							<span className="card__moreHint">Another hour, or five more — whichever is fewer</span>
						</button>
					</article>
				) : null}
			</div>
			<div className="deck__dots" aria-hidden="true">
				{plans.map((plan, index) => (
					<span
						key={plan.id}
						className={[
							"deck__dot",
							index === selectedIndex ? "is-active" : "",
							plan.id === snapshot.recommendedId ? "is-recommended" : "",
						].join(" ")}
					/>
				))}
				{moreAvailable ? <span className="deck__dot deck__dot--more" /> : null}
			</div>
		</div>
	);
}

/**
 * At hero size the en dash lands in a full tabular-numeral cell and the negative letter
 * spacing drags the digits into it, so the separator gets its own metrics.
 */
function heroContent(hero: string) {
	const [from, to, ...rest] = hero.split("–");
	if (to == null || rest.length > 0) {
		return hero;
	}
	return (
		<>
			{from}
			<span className="card__range">–</span>
			{to}
		</>
	);
}

/**
 * The last leg, once the trains have nothing left to say.
 *
 * One card, not a deck: there is nothing to swipe between. Where he is heading is a choice on
 * the way home, so the alternatives sit in the facts the same way the two stops do on the train.
 */
export function OnwardCard({
	legs,
	chosen,
	homeward,
	saying,
	onChoose,
	pickup = null,
	onRequestPickup,
	onStandDownPickup,
	nowMs,
}: {
	legs: OnwardLeg[];
	chosen: number;
	/** The greeting when he gets there, and nothing else. */
	homeward: boolean;
	/** Today's line under that greeting, chosen by the worker. */
	saying: string | null;
	onChoose: (index: number) => void;
	/**
	 * The meeting, if there is one.
	 *
	 * This card is the one he is looking at while he stands on the curb waiting, which is the
	 * moment the pickup matters most — so it has to appear here as well as on the deck.
	 */
	pickup?: PickupState | null;
	onRequestPickup?: () => void;
	onStandDownPickup?: () => void;
	nowMs: number;
}) {
	const done = legs.length === 0;
	const leg = done ? null : (legs[chosen] ?? legs[0] ?? null);
	const copy = onwardStatus(leg, homeward, saying);
	// The last leg is a drive someone has to make, and this is the only card that knows where to.
	const driveTo = leg ? { id: leg.id, name: leg.label, lat: leg.place.lat, lon: leg.place.lon } : null;

	return (
		<div className="deck">
			<div className="deck__scroller">
				<article className={`card tone-${copy.tone}`} aria-current="true">
					<header className="card__tag">
						<span className="card__tag">{done ? "Journey complete" : "Last leg"}</span>
						<span className="pill pill--good">{done ? "ARRIVED" : "OFF THE TRAIN"}</span>
					</header>

					<div className="card__hero">
						{copy.lead ? <p className="card__lead">{copy.lead}</p> : null}
						<div className="card__valueRow">
							<p className={done ? "card__value card__value--greeting" : "card__value"}>
								{heroContent(copy.hero)}
								{copy.unit ? <span className="card__unit">{copy.unit}</span> : null}
							</p>
							{driveTo ? <NavigateLinks target={driveTo} label={`Navigate to ${leg!.label}`} /> : null}
						</div>
						{copy.sub ? <p className={done ? "card__action card__action--saying" : "card__action"}>{copy.sub}</p> : null}
					</div>

					{legs.length > 0 ? (
						/* Both houses, in the order they are configured, the same as the two homeward stops
						   on the train: a control that moves is one you have to read before you can press. */
						<dl className="card__facts">
							{legs.map((leg, index) => (
								<div key={leg.label} className={`card__fact--swap ${index === chosen ? "is-chosen" : ""}`}>
									<dt>
										<button type="button" className="card__factButton" aria-pressed={index === chosen} onClick={() => onChoose(index)}>
											{leg.label}
										</button>
									</dt>
									<dd>
										{(leg.roadMeters ?? leg.metersAway) == null ? "—" : ((leg.roadMeters ?? leg.metersAway)! / 1609.344).toFixed(1)}
										<span className="card__factUnit">miles</span>
									</dd>
								</div>
							))}
						</dl>
					) : null}

					{pickup ? (
						<PickupBlock pickup={pickup} nowMs={nowMs} onRequest={onRequestPickup} onStandDown={onStandDownPickup} />
					) : null}
				</article>
			</div>
		</div>
	);
}

function PlanCard({
	plan,
	snapshot,
	nowMs,
	preview,
	mode,
	destinationIndex,
	onChooseDestination,
	driveTarget,
	pinnedId,
	guessedId,
	pickup,
	onRequestPickup,
	onStandDownPickup,
	cardOverride,
}: {
	plan: CommutePlan;
	snapshot: CommuteSnapshot;
	nowMs: number;
	preview: boolean;
	mode: CardMode;
	destinationIndex: number;
	onChooseDestination: (index: number) => void;
	driveTarget: NavTarget | null;
	pinnedId: string | null;
	guessedId: string | null;
	pickup: PickupState | null;
	onRequestPickup?: () => void;
	onStandDownPickup?: () => void;
	cardOverride: CardStatus | null;
}) {
	const hasTravelLeg = snapshot.profile.hasAccessLeg;
	// Once he is aboard, none of the getting-there numbers mean anything: the drive is over, the
	// boarding time is behind him, and a verdict on whether he will make it is just wrong.
	const onboard = !preview && mode === "onboard";
	// A preview is about another day, where nobody is driving anywhere yet.
	const copy = onboard && cardOverride ? cardOverride : cardStatus(plan, hasTravelLeg, nowMs, snapshot.timezone, preview ? "leave" : mode, destinationIndex);
	const isRecommended = !preview && plan.id === snapshot.recommendedId;
	// Aboard, the pill says which station is behind him, the same as the board below. Whether he
	// is on this train is the caption's job, and saying it twice left every card claiming it.
	const tag = preview ? previewTag(plan, snapshot.previewRecommendedId) : planTag(plan, snapshot, mode, nowMs);
	/*
	 * Whose train this is.
	 *
	 * He says which one he is on and that stands. The guess is the phone's own record talking —
	 * the last time it saw him on the platform, and the first train to leave after that — and when
	 * the two disagree the disagreement is the interesting part, so both are labelled.
	 */
	const caption = preview
		? "Target day"
		: onboard && plan.id === pinnedId
			? "On board"
			: onboard && guessedId != null && plan.id === guessedId
				? "Maybe on board"
				: isRecommended
					? "Recommended"
					: "Alternative";
	const claimed = caption === "On board" || caption === "Maybe on board";
	const delaySeconds = plan.boarding.source === "realtime" ? plan.boarding.delaySeconds : null;
	const delayLabel = delaySeconds != null && Math.abs(delaySeconds) >= 60 ? `${signedMinutes(delaySeconds)} min` : null;

	const minutesToTrain = Math.max(0, Math.round((plan.boarding.predictedMs - nowMs) / 60_000));
	const minutesToStation =
		plan.stationArrivalMs == null ? null : Math.max(0, Math.round((plan.stationArrivalMs - nowMs) / 60_000));
	// Standing on the platform, the numbers he does not already have are what the wait becomes if
	// he lets this one go, and then the one after that. Taken from every departure the engine
	// looked at, not from the deck, which is trimmed and leaves the last card with nothing.
	const laterWaits = snapshot.departuresMs
		.filter((departureMs) => departureMs > plan.boarding.predictedMs)
		.sort((a, b) => a - b)
		.slice(0, 2)
		.map((departureMs) => Math.max(0, Math.round((departureMs - nowMs) / 60_000)));
	// Where he is actually going replaces the platform he gets off at, rather than joining it.
	const showsFinal = snapshot.profile.finalLabel != null && plan.finalArrivalMs != null;
	// The ride home can have several stops he might get off at. All are offered, in the order the
	// profile names them, so none of them move when he changes his mind — and this is available
	// from the moment he is still at school, not only once he is aboard, because the choice is
	// his to make before he is standing on the platform he did not mean to be on.
	const choices = plan.destinations.length > 1 ? plan.destinations : null;
	// The choice is an index into the profile's stops, which is not the plan's own list when a
	// train skips one of them.
	const profileIndexOf = (stopId: string) => {
		const index = snapshot.profile.destinations.findIndex((station) => station.id === stopId);
		return index >= 0 ? index : 0;
	};
	// Where he gets off. Aboard, when the train reaches it is the only time on the card worth
	// reading: the platform it left from is behind him. Before that, it is whichever stop the
	// shared schedule already names for today, until he picks a different one.
	const alighting = plan.destinations[destinationIndex] ?? plan.destinations[0] ?? null;
	const navigationLabel = driveTarget ? `Navigate to ${driveTarget.name}` : null;
	// Both (or all three) stops, always in the same order, each under its own name, laid out as
	// their own row so they never compete for grid columns with the primary train-time fact — a
	// four-item row once squeezed "11:xx"-style times right past their own box edge.
	const destinationChoices = choices ? (
		<div className="card__fact--row">
			{choices.map((stop) => {
				const chosen = stop.stopId === alighting?.stopId;
				return (
					<div key={stop.stopId} className={`card__fact--swap ${chosen ? "is-chosen" : ""}`}>
						<dt>
							<button
								type="button"
								className="card__factButton"
								aria-pressed={chosen}
								onClick={() => onChooseDestination(profileIndexOf(stop.stopId))}
							>
								{stationLabel(stop.stopName)}
							</button>
						</dt>
						<dd>
							<Clock ms={stop.predictedMs} timezone={snapshot.timezone} superscript />
						</dd>
					</div>
				);
			})}
		</div>
	) : null;

	return (
		<article className={`card tone-${copy.tone}`} aria-current={isRecommended ? "true" : undefined}>
			<header className="card__tag">
				<span className={`card__tag ${isRecommended || claimed ? "" : "card__tag--muted"}`}>{caption}</span>
				{delayLabel ? <span className="card__delay" title={`${Math.abs(Math.round(delaySeconds! / 60))} minutes ${delaySeconds! > 0 ? "late" : "early"} versus schedule`}>{delayLabel}</span> : null}
				<span className={`pill pill--${tag.tone}`}>{tag.text}</span>
			</header>

			<div className="card__hero">
				{copy.lead ? <p className="card__lead">{copy.lead}</p> : null}
				<div className="card__valueRow">
					<p className="card__value">
						{heroContent(copy.hero)}
						{copy.heroMeridiem ? <span className="clock__meridiem">{copy.heroMeridiem}</span> : null}
						{copy.unit ? <span className="card__unit">{copy.unit}</span> : null}
					</p>
					{driveTarget && navigationLabel && !preview ? <NavigateLinks target={driveTarget} label={navigationLabel} /> : null}
				</div>
				<div className="card__actionRow">
					<p className="card__action">
						{copy.subParts
							? copy.subParts.map((part, index) =>
									part.time ? (
										<span key={index} className="card__actionTime">
											{part.text}
										</span>
									) : (
										<Fragment key={index}>{part.text}</Fragment>
									),
								)
							: copy.sub}
					</p>
					{copy.note ? (
						<p className={`card__note card__note--${copy.note.tone}`}>
							<span className="card__noteValue">{copy.note.value}</span> {copy.note.label}
						</p>
					) : null}
				</div>
			</div>

			<dl className="card__facts">
				{onboard && destinationChoices ? (
					destinationChoices
				) : (
					<>
						{hasTravelLeg && !preview && !onboard ? (
					<>
						{/* While driving, the useful comparison is these two side by side. On the platform
						    the drive is over and this train is already the hero, so both slots go to the
						    fallback: the wait he takes on by letting this one go, and the one after that.
						    Their clock times used to sit here and read as arrival times. */}
						{mode === "platform" ? (
							<>
								<div>
									<dt>Next train</dt>
									<dd>
										{laterWaits[0] == null ? (
											"\u2014"
										) : (
											<>
												{laterWaits[0]}
												<span className="card__factUnit">min</span>
											</>
										)}
									</dd>
								</div>
								<div>
									<dt>And then</dt>
									<dd>
										{laterWaits[1] == null ? (
											"\u2014"
										) : (
											<>
												{laterWaits[1]}
												<span className="card__factUnit">min</span>
											</>
										)}
									</dd>
								</div>
							</>
						) : (
							<>
								<div>
									<dt>Train in</dt>
									<dd>
										{minutesToTrain}
										<span className="card__factUnit">min</span>
									</dd>
								</div>
								<div className={minutesToStation != null && minutesToStation >= minutesToTrain ? "is-warn" : undefined}>
									<dt>Drive time</dt>
									<dd>
										{minutesToStation ?? "--"}
										<span className="card__factUnit">min</span>
									</dd>
								</div>
							</>
						)}
					</>
				) : (
					<div>
						{/* Aboard, the station names itself; before that this is the departure. */}
						<dt>{onboard && alighting ? stationLabel(alighting.stopName) : "Train"}</dt>
						<dd>
							<Clock ms={onboard ? (alighting?.predictedMs ?? plan.boarding.predictedMs) : plan.boarding.predictedMs} timezone={snapshot.timezone} superscript />
						</dd>
					</div>
				)}

				{/*
				 * Before boarding, the destination is still a choice, not a fact — so it gets the
				 * same pressable style as aboard, just underneath the train time rather than
				 * instead of it. It already defaults to whichever stop today's shared schedule
				 * names, same as the pinned card does once he is on the train. A single-stop
				 * profile has nothing to choose between, so it keeps the plain, unpressable fact.
				 */}
				{!onboard && destinationChoices
					? destinationChoices
					: plan.destinations.map((destination) =>
							showsFinal ? null : (
								<div key={destination.stopId}>
									<dt>{stationLabel(destination.stopName)}</dt>
									<dd>
										<Clock ms={destination.predictedMs} timezone={snapshot.timezone} superscript />
									</dd>
								</div>
							),
						)}

				{showsFinal ? (
					<div>
						<dt>{snapshot.profile.finalLabel}</dt>
						<dd>
							<Clock ms={plan.finalArrivalMs} timezone={snapshot.timezone} superscript />
						</dd>
					</div>
				) : null}
					</>
				)}
			</dl>

			{pickup ? (
				<PickupBlock pickup={pickup} nowMs={nowMs} onRequest={onRequestPickup} onStandDown={onStandDownPickup} />
			) : null}
		</article>
	);
}
